import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useBalanceContext } from "../../contexts/BalanceContext";
import { useGameCounts } from "../../hooks/useGameCounts";
import VIPStatusCard from "../components/VIPStatusCard";
import affiliateService from "../services/affiliateService";
import {
  LobbyIcon,
  CoinPackagesIcon,
  SlotsIcon,
  TableGamesIcon,
  LiveCasinoIcon,
  JackpotsIcon,
  NewGamesIcon,
  FavoritesIcon,
  RecentlyPlayedIcon,
  PromotionsIcon,
  VIPIcon,
  ProvidersIcon,
  ReferFriendsIcon,
  AffiliatesIcon,
  HelpCenterIcon,
} from "../components/icons";
import { PrimaryButton, TertiaryButton } from "../components/buttons";

/**
 * Persistent Casino Sidebar - Renders once and stays stable
 * No re-rendering during navigation to prevent flickering
 */
// Receives `collapsed` and `onToggle` from PersistentCasinoLayout, which
// owns the state. Owning it in one place removes the cross-component
// race that used to bridge two useState copies via a window CustomEvent.
const CasinoSidebar = ({ collapsed: sidebarCollapsed, onToggle }) => {
  const { user, isLoggedIn } = useAuth();
  const { balances } = useBalanceContext();
  const { counts: gameCounts } = useGameCounts();
  const navigate = useNavigate();
  const location = useLocation();

  const [isApprovedAffiliate, setIsApprovedAffiliate] = useState(false);

  // Check affiliate status when logged in
  useEffect(() => {
    if (isLoggedIn) {
      affiliateService
        .getStatus()
        .then((status) => {
          setIsApprovedAffiliate(
            status.isAffiliate && status.affiliateStatus === "approved",
          );
        })
        .catch(() => setIsApprovedAffiliate(false));
    } else {
      setIsApprovedAffiliate(false);
    }
  }, [isLoggedIn]);

  // Memoized function to determine selected category from current path
  const selectedCategory = useMemo(() => {
    const path = location.pathname;
    if (path === "/" || path === "/lobby") return "lobby";
    if (
      path === "/offers" ||
      path.startsWith("/offers") ||
      path.startsWith("/checkout")
    )
      return "coin-packages";

    if (path.startsWith("/games/slots")) return "slots";
    if (path.startsWith("/games/table")) return "table";
    if (path.startsWith("/games/live")) return "live";
    if (path.startsWith("/games/instant")) return "instant";
    if (path.startsWith("/games/new")) return "new";
    if (path.startsWith("/games/favorites")) return "favorites";
    if (path.startsWith("/games/recent")) return "recent";
    if (path.startsWith("/providers") || path.startsWith("/games/provider"))
      return "providers";
    if (path.startsWith("/promotions")) return "promotions";
    if (path.startsWith("/vip")) return "vip";
    if (path.startsWith("/blog")) return "guides";
    if (path.startsWith("/redeem")) return null; // don't highlight anything on redeem page
    if (path.startsWith("/refer")) return "refer";
    if (path.startsWith("/affiliate")) return "affiliate";
    return "lobby"; // default fallback
  }, [location.pathname]);

  // Use CSS-based approach - set data attribute on sidebar container
  useEffect(() => {
    const path = location.pathname;
    let newCategory = selectedCategory;

    if (path === "/" || path === "/lobby") newCategory = "lobby";
    else if (path === "/offers" || path.startsWith("/offers"))
      newCategory = "coin-packages";
    else if (path.startsWith("/games/slots")) newCategory = "slots";
    else if (path.startsWith("/games/table")) newCategory = "table";
    else if (path.startsWith("/games/live")) newCategory = "live";
    else if (path.startsWith("/games/instant")) newCategory = "instant";
    else if (path.startsWith("/games/new")) newCategory = "new";
    else if (path.startsWith("/games/favorites")) newCategory = "favorites";
    else if (path.startsWith("/games/recent")) newCategory = "recent";
    else if (
      path.startsWith("/providers") ||
      path.startsWith("/games/provider")
    )
      newCategory = "providers";
    else if (path.startsWith("/promotions")) newCategory = "promotions";
    else if (path.startsWith("/vip")) newCategory = "vip";

    const timer = setTimeout(() => {
      document.documentElement.style.setProperty(
        "--active-category",
        `"${newCategory}"`,
      );
    }, 100);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  // Active-state border style — three-stop gold gradient painted into the
  // border via background-clip:border-box, mirroring the BIG WIN card
  // treatment in SocialProofCard. The inner padding-box layer is the
  // warm-neutral page bg (#18181b) rather than the chrome gray (which has
  // a subtle blue undertone in OKLCH) so the active item reads as gold-
  // framed warm dark instead of gold-framed blue-tinted.
  const activeBorderStyle = {
    background:
      "linear-gradient(#18181b, #18181b) padding-box, linear-gradient(135deg, #FFE48A 0%, #F5B955 50%, #A87A1F 100%) border-box",
    border: "1px solid transparent",
  };

  // Memoized navigation handler to prevent unnecessary re-renders
  const handleSidebarNavigation = useCallback(
    (itemId) => {
      if (itemId === "lobby") {
        navigate("/");
      } else if (itemId === "coin-packages") {
        navigate("/offers");
      } else if (itemId === "slots") {
        navigate("/games/slots");
      } else if (itemId === "table") {
        navigate("/games/table");
      } else if (itemId === "live") {
        navigate("/games/live");
      } else if (itemId === "instant") {
        navigate("/games/instant");
      } else if (itemId === "new") {
        navigate("/games/new");
      } else if (itemId === "favorites") {
        navigate("/games/favorites");
      } else if (itemId === "recent") {
        navigate("/games/recent");
      } else if (itemId === "providers") {
        navigate("/providers");
      }
    },
    [navigate],
  );

  // Memoized sidebar navigation items to prevent unnecessary re-renders
  const sidebarItems = useMemo(() => {
    const allItems = [
      {
        id: "lobby",
        name: "Lobby",
        icon: (isActive) => (
          <LobbyIcon className="w-5 h-5" isActive={isActive} />
        ),
        count: null,
        requiresAuth: false,
      },
      {
        id: "coin-packages",
        name: "Coin Packages",
        icon: (isActive) => (
          <CoinPackagesIcon className="w-5 h-5" isActive={isActive} />
        ),
        count: null,
        requiresAuth: true,
      },
      {
        id: "providers",
        name: "Providers",
        icon: (isActive) => (
          <ProvidersIcon className="w-5 h-5" isActive={isActive} />
        ),
        count: null,
        requiresAuth: false,
      },
      {
        id: "new",
        name: "New",
        icon: (isActive) => (
          <NewGamesIcon className="w-5 h-5" isActive={isActive} />
        ),
        count: gameCounts.new || null,
        requiresAuth: false,
      },
      {
        id: "slots",
        name: "Slots",
        icon: (isActive) => (
          <SlotsIcon className="w-5 h-5" isActive={isActive} />
        ),
        count: gameCounts.slots || null,
        requiresAuth: false,
      },
      {
        id: "live",
        name: "Live",
        icon: (isActive) => (
          <LiveCasinoIcon className="w-5 h-5" isActive={isActive} />
        ),
        count: gameCounts.live_casino || null,
        requiresAuth: false,
      },
      {
        id: "table",
        name: "Table",
        icon: (isActive) => (
          <TableGamesIcon className="w-5 h-5" isActive={isActive} />
        ),
        count: gameCounts.table_games || null,
        requiresAuth: false,
      },
      {
        id: "instant",
        name: "Instant",
        icon: (isActive) => (
          <JackpotsIcon className="w-5 h-5" isActive={isActive} />
        ),
        count: gameCounts.instant || null,
        requiresAuth: false,
      },
      {
        id: "favorites",
        name: "Favorites",
        icon: (isActive) => (
          <FavoritesIcon className="w-5 h-5" isActive={isActive} />
        ),
        count: null,
        requiresAuth: true,
      },
      {
        id: "recent",
        name: "Recently Played",
        icon: (isActive) => (
          <RecentlyPlayedIcon className="w-5 h-5" isActive={isActive} />
        ),
        count: null,
        requiresAuth: true,
      },
    ];

    // Filter out auth-required items for non-logged-in users
    return allItems.filter((item) => !item.requiresAuth || isLoggedIn);
  }, [gameCounts, isLoggedIn]);

  // Memoized additional sidebar items (below divider)
  const additionalItems = useMemo(() => {
    const allAdditionalItems = [
      {
        id: "guides",
        name: "Guides",
        icon: (isActive) => (
          <HelpCenterIcon className="w-5 h-5" isActive={isActive} />
        ),
        action: () => navigate("/blog"),
        requiresAuth: false,
      },
      {
        id: "promotions",
        name: "Promotions",
        icon: (isActive) => (
          <PromotionsIcon className="w-5 h-5" isActive={isActive} />
        ),
        action: () => navigate("/promotions"),
        requiresAuth: true,
      },
      {
        id: "vip",
        name: "VIP",
        icon: (isActive) => <VIPIcon className="w-5 h-5" isActive={isActive} />,
        action: () => navigate("/vip"),
        requiresAuth: true,
      },
      // Show "Affiliate" for approved affiliates, "Refer Friends" for others
      isApprovedAffiliate
        ? {
            id: "affiliate",
            name: "Affiliate",
            icon: (isActive) => (
              <AffiliatesIcon className="w-5 h-5" isActive={isActive} />
            ),
            action: () => navigate("/affiliate"),
            requiresAuth: true,
          }
        : {
            id: "refer",
            name: "Refer Friends",
            icon: (isActive) => (
              <ReferFriendsIcon className="w-5 h-5" isActive={isActive} />
            ),
            action: () => navigate("/refer"),
            requiresAuth: true,
          },
    ];

    // Filter out auth-required items for non-logged-in users
    return allAdditionalItems.filter(
      (item) => !item.requiresAuth || isLoggedIn,
    );
  }, [navigate, isLoggedIn, isApprovedAffiliate]);

  // Memoised auth snapshot. Only re-allocates when login state or the user
  // identity actually changes; route navigation alone won't bust it.
  const frozenAuthState = useMemo(
    () => ({
      isLoggedIn,
      user,
      balances,
    }),
    [isLoggedIn, user?.id, balances],
  );

  return (
    <div
      className={`fixed left-0 top-0 h-full bg-card transition-all duration-300 z-50 flex flex-col overflow-hidden
      hidden md:flex
      ${sidebarCollapsed ? "w-16" : "w-64"}
    `}
    >
      {/* Logo/Brand */}
      <div className="h-16 flex items-center justify-center px-4 flex-shrink-0 relative">
        <img
          src={sidebarCollapsed ? "/Logo-symbol.png" : "/logo.png"}
          alt="Cluck Casino"
          className={`object-contain transition-all cursor-pointer ${
            sidebarCollapsed ? "w-10 h-10" : "h-12 w-auto"
          }`}
          onClick={() => navigate("/")}
        />
      </div>

      {/* VIP Status Section - Only render when logged in */}
      {!sidebarCollapsed && frozenAuthState.isLoggedIn && (
        <div className="mx-2 mt-4 mb-4 flex-shrink-0">
          <VIPStatusCard collapsed={sidebarCollapsed} />
        </div>
      )}

      {/* Buy Coins + Redeem, only render when logged in.
          size="small" keeps the tactile gold pill proportional in the
          narrow sidebar slot. Redeem stays TertiaryButton (outline) so
          it recedes cleanly next to the gold primary. */}
      {!sidebarCollapsed && frozenAuthState.isLoggedIn && (
        <div className="mx-2 mb-4 space-y-2">
          <PrimaryButton
            onClick={() => navigate("/offers")}
            className="w-full"
            size="small"
          >
            Buy Coins
          </PrimaryButton>

          <TertiaryButton
            onClick={() => navigate("/redeem")}
            className="w-full"
            size="small"
          >
            Redeem
          </TertiaryButton>
        </div>
      )}

      {/* All Navigation Items - Single Scrollable Container */}
      <nav className={`flex-1 px-2 overflow-y-auto pt-2 pb-4`}>
        <div className="min-h-full flex flex-col">
          {/* Main Navigation Items */}
          {/* Main Navigation Items.
              All icons render in their gold "active" style by passing
              isActive={true} unconditionally — gold becomes the brand
              accent on every row. The active-state highlight is JUST a
              gradient gold border (mirroring the BIG WIN card from
              Recent Activity); inactive items carry a transparent 1px
              border so the inner content doesn't jump on selection. */}
          <div className="space-y-1">
            {sidebarItems.map((item) => {
              const isActive = selectedCategory === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => handleSidebarNavigation(item.id)}
                  className={`w-full flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer focus:outline-none ${
                    isActive
                      ? "text-white"
                      : "text-foreground border border-transparent hover:bg-muted hover:text-white"
                  }`}
                  style={isActive ? activeBorderStyle : undefined}
                >
                  <span className="text-lg mr-3 flex items-center justify-center w-5 h-5">
                    {typeof item.icon === "string"
                      ? item.icon
                      : typeof item.icon === "function"
                        ? item.icon(true)
                        : item.icon}
                  </span>
                  {!sidebarCollapsed && (
                    <span className="flex-1 flex items-center justify-between min-w-0">
                      <span className="text-left truncate">{item.name}</span>
                      {typeof item.count === "number" && item.count > 0 && (
                        <span className="ml-2 text-xs text-muted-foreground font-normal tabular-nums">
                          {item.count.toLocaleString()}
                        </span>
                      )}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Spacer to push additional items to bottom */}
          <div className="flex-1"></div>

          {/* Additional Items — same gold-icon + gold-gradient-border-on-active pattern. */}
          <div className="space-y-1">
            {additionalItems.map((item) => {
              const isActive = selectedCategory === item.id;
              return (
                <button
                  key={item.id}
                  onClick={item.action}
                  className={`w-full flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer focus:outline-none ${
                    isActive
                      ? "text-white"
                      : "text-foreground border border-transparent hover:bg-muted hover:text-white"
                  }`}
                  style={isActive ? activeBorderStyle : undefined}
                >
                  <span className="text-lg mr-3 flex items-center justify-center w-5 h-5">
                    {typeof item.icon === "string"
                      ? item.icon
                      : typeof item.icon === "function"
                        ? item.icon(true)
                        : item.icon}
                  </span>
                  {!sidebarCollapsed && (
                    <span className="flex-1 text-left">{item.name}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Collapse Button - Desktop only */}
      <div className="hidden md:block p-2 flex-shrink-0">
        <button
          onClick={onToggle}
          className="w-full flex items-center justify-center p-2 text-gray-400 hover:text-white hover:bg-muted rounded-lg transition-colors cursor-pointer focus:outline-none"
        >
          <svg
            className={`w-5 h-5 transition-transform ${sidebarCollapsed ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default CasinoSidebar;
